// shared-lib/cabinet-runs/cornerChoice.js
//
// The answers a right-angled corner can be given — by the plan, or by the
// user — in ONE vocabulary for both tiers. A lower corner and the upper corner
// above it are different decisions made against different obstacles
// (`lowerCornerTreatment.js`, `upperCornerTreatment.js`), but they are the same
// four shapes: a corner unit, either leg running to the vertex, or neither.
//
// Pure. No I/O.

const { legTakes } = require('../kitchen-assembly/cornerBlind');

/** Every answer a right-angled corner can be given, in menu order. */
const CORNER_OPTIONS = [
  { id: 'cabinet', mode: 'cabinet' },
  { id: 'blindA', mode: 'blind', extend: 'A' },
  { id: 'blindB', mode: 'blind', extend: 'B' },
  { id: 'empty', mode: 'empty' },
];

/**
 * Which field of a corner's record carries the USER's pick.
 *
 * Not the same one in both tiers, and on purpose. A LOWER record's `treatment`
 * is the generator's — derived from what it built, trusted unchecked — so the
 * user's pick sits beside it as `userTreatment` and clearing it puts the
 * generator's corner back. Nothing else ever wrote an UPPER record, so there
 * the pick IS `treatment`. Every reader and writer asks here.
 */
const userChoiceField = (tier) =>
  tier === 'upper' ? 'treatment' : 'userTreatment';

/** The option a treatment names, or null for one these tiers do not offer. */
function optionIdOf(treatment) {
  if (treatment?.mode === 'cabinet') return 'cabinet';
  if (treatment?.mode === 'empty') return 'empty';
  if (treatment?.mode === 'blind')
    return treatment.extend === 'B' ? 'blindB' : 'blindA';
  return null;
}

/** The treatment an option id stands for — what a record stores. */
function treatmentOf(optionId) {
  const opt = CORNER_OPTIONS.find((o) => o.id === optionId);
  if (!opt) return null;
  return opt.extend
    ? { mode: opt.mode, extend: opt.extend }
    : { mode: opt.mode };
}

/**
 * The reason one leg is too short for an option, or null when neither is.
 * `legs` is `{ A: { clear, blocker?, blockerId? }, B: … }`; leg A is asked
 * first, so a reason always names ONE leg — the first that fails.
 */
function shortLeg(legs, needA, needB = needA) {
  for (const [leg, need] of [
    ['A', needA],
    ['B', needB],
  ]) {
    const at = legs[leg];
    if (legTakes(at.clear, need)) continue;
    return {
      code: 'leg-too-short',
      leg,
      need,
      clear: at.clear,
      blocker: at.blocker ?? null,
      blockerId: at.blockerId ?? null,
    };
  }
  return null;
}

const IN = 0.0254;

/**
 * The SIZE half of a blind corner's report — what unit stands there, what the
 * user asked for, and what the family sells that fits — in metres, from a
 * grade made in inches (`kitchen-assembly/cornerBlind.blindSizeChoices`).
 * Same shape for both tiers, so one control reads it.
 *
 * @param {Object} choices `blindSizeChoices(...)` for the leg
 * @param {{ built: number, requested: number|null, honoured: boolean|null,
 *   door: number }} unit metres; `door` is the built unit's reachable leaf
 */
function blindSizeReport(choices, { built, requested, honoured, door }) {
  const base = { built, requested, honoured, door };
  if (choices.continuous)
    return {
      ...base,
      continuous: true,
      min: choices.min * IN,
      max: choices.max * IN,
      feasible: choices.feasible,
      options: [],
    };
  return {
    ...base,
    continuous: false,
    options: choices.sizes.map((o) => ({
      length: o.size * IN,
      feasible: o.feasible,
    })),
  };
}

module.exports = {
  CORNER_OPTIONS,
  blindSizeReport,
  userChoiceField,
  optionIdOf,
  treatmentOf,
  shortLeg,
};
